import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { divisionListQueryOptions } from '@/features/geo/api/geo.api';
import { leaveTypesQueryOptions } from '@/features/leaveTypes/api/leaveTypes.api';
import { useAmZmFilterOptions } from '@/hooks/useAmZmFilterOptions';
import { useDistrictThanaFilterOptions } from '@/hooks/useDistrictThanaFilterOptions';
import { withOptionCounts } from '@/utils/filters';
import { leaveApplicationCountsBySubmitterQueryOptions } from '../api/leaveApplications.api';
import type { LeaveApplicationSummaryParams } from '../types/leaveApplication.types';
import { getLeaveApplicationFilterFields } from '../utils/getLeaveApplicationFilterFields';
import { toCountsBySubmitterParams } from '../utils/leaveApplicationFilters';

interface UseLeaveApplicationFilterFieldsParams {
  isEnabled: boolean;
  isAttendanceClaim: boolean;
  summaryParams: LeaveApplicationSummaryParams;
}

export function useLeaveApplicationFilterFields({
  isEnabled,
  isAttendanceClaim,
  summaryParams,
}: UseLeaveApplicationFilterFieldsParams) {
  const {
    amOptions,
    zmOptions,
    hasOptionsError: hasHierarchyOptionsError,
  } = useAmZmFilterOptions({ isEnabled, amUserId: summaryParams.amUserId });
  const { data: divisions, isError: hasDivisionsError } = useQuery({
    ...divisionListQueryOptions(),
    enabled: isEnabled,
  });
  const {
    districts,
    thanas,
    hasOptionsError: hasGeoOptionsError,
  } = useDistrictThanaFilterOptions({
    isEnabled,
    divisionId: summaryParams.divisionId,
    districtId: summaryParams.districtId,
  });
  const countsBySubmitterParams = useMemo(
    () => toCountsBySubmitterParams(summaryParams),
    [summaryParams],
  );
  const { data: countsBySubmitter, isError: hasCountsError } = useQuery({
    ...leaveApplicationCountsBySubmitterQueryOptions(countsBySubmitterParams),
    enabled: isEnabled,
  });
  const { data: leaveTypes, isError: hasLeaveTypesError } = useQuery({
    ...leaveTypesQueryOptions(),
    enabled: isEnabled,
  });

  const fields = useMemo(
    () =>
      getLeaveApplicationFilterFields({
        isAttendanceClaim,
        hasDistrict: summaryParams.districtId !== undefined,
        divisions,
        districts,
        thanas,
        amOptions: withOptionCounts(amOptions, countsBySubmitter?.amCounts),
        zmOptions: withOptionCounts(zmOptions, countsBySubmitter?.zmCounts),
        leaveTypes,
      }),
    [
      isAttendanceClaim,
      summaryParams.districtId,
      divisions,
      districts,
      thanas,
      amOptions,
      zmOptions,
      countsBySubmitter,
      leaveTypes,
    ],
  );

  return {
    fields,
    hasOptionsError:
      hasHierarchyOptionsError ||
      hasDivisionsError ||
      hasGeoOptionsError ||
      hasCountsError ||
      hasLeaveTypesError,
  };
}
